import { useL10n } from "@ews/react-localization-context";
import { InputChangeEventDetail, InputInputEventDetail } from "@ionic/core";
import { IonButton, IonCheckbox, IonIcon, IonInput, IonItem, IonLabel, IonText } from "@ionic/react";
import React, { useEffect, useRef } from "react";
import { useIsMobile } from "../../../Responsive";
import globalStyle from '../GlobalSettings.module.scss';
import style from './SettingsCheckboxTextInput.module.scss';
import { SettingsCheckboxTextInputProps } from "./types";

const SettingsCheckboxTextInput: React.FC<SettingsCheckboxTextInputProps> = ({
    endText = '',
    startText = '',
    onChange,
    textAlign,
    inputType = 'text',
    value,
    hidden = false,
    labelPosition = 'stacked',
    text = '',
    disabled,
    hasFocus,
    required,
    lines = undefined,
    debounce,
    tooltip,
    onFocus,
    onClick,
    onBlur,
    onClear,
    onEnter,
    id,
    placeholder,
    inputRef,
    icon,
    onIconClick,
    onKeyDown,
    className
}) =>
{
    const { translate: t } = useL10n();
    const mobileView = useIsMobile();
    const inputClass = mobileView ? globalStyle.mobileInput : globalStyle.webInput;
    const InputValue = useRef<string | null | undefined>(value);
    const innerInputRef = useRef<HTMLIonInputElement>(null);

    const _hasFocus = useRef<boolean>(false);

    const getInputValue = () =>
    {
        return inputRef?.current?.value?.toString() || innerInputRef?.current?.value?.toString();
    };

    const handleFocusAfterReload = (ref: React.RefObject<HTMLIonInputElement>) =>
    {
        ref.current?.setFocus();
    };

    const handleFocusOut = () =>
    {
        _hasFocus.current = false;
    };
    const handleFocusIn = () =>
    {
        _hasFocus.current = true;
    };

    const handleChange = (detail: InputInputEventDetail | InputChangeEventDetail) =>
    {
        InputValue.current = detail.value;
        if (onChange) onChange(detail.value || '');
    };

    if (hasFocus) handleFocusAfterReload(inputRef || innerInputRef);

    useEffect(() =>
    {
        InputValue.current = value;
    }, [value]);

    return <IonItem
        data-testid="settings-input-container"
        hidden={hidden}
        lines={lines}
        className={`${style.overflow} ${globalStyle.setting} ${hasFocus ? style.hasFocus : ''}`}>
        <div style={{ display: "flex", alignItems: "center", width: "100%", gap: 16 }}>
            <div style={{ width: "fit-content !important" }}>
                <IonCheckbox disabled={disabled} checked={!!value} style={{ marginRight: 8 }} />

            </div>
            {text &&
                <IonLabel
                    data-testid="settings-input-label"
                    className={style.label}
                    style={{
                        width: "33%",
                        minWidth: 0,
                        margin: "10px 8px 10px 0",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis"
                    }}
                    position={labelPosition}
                >
                    {`${startText}${t(text)}${endText}`}{required ? <span className={`${globalStyle.required}`}>*</span> : null}
                    {!mobileView && tooltip && <span className={style.tooltip}>{tooltip}</span>}
                </IonLabel>
            }

            {/* <IonText style={{ width: "33%", margin: "10px 8px 10px 0", fontSize: "20px !important" }}>
                {`${startText}${t(text)}${endText}`}{required ? <span className={`${globalStyle.required}`}>*</span> : null}
                {!mobileView && tooltip && <span className={style.tooltip}>{tooltip}</span>}
            </IonText> */}
            <div style={{ flex: 1 }}>
                <IonInput
                    data-testid="settings-input"
                    placeholder={placeholder}
                    id={id || undefined}
                    label={``}
                    debounce={debounce}
                    autocomplete={'off'}
                    ref={inputRef || innerInputRef}
                    onClick={(e) => { if (onClick) onClick(e); }}
                    onIonFocus={(e) =>
                    {
                        handleFocusIn();
                        if (onFocus) onFocus(e);
                    }}
                    onIonBlur={(e) =>
                    {
                        handleFocusOut();
                        if (onBlur) onBlur({ value: InputValue.current, focus: _hasFocus.current, event: e });
                    }}
                    required={required}
                    value={value}
                    type={inputType}
                    className={`${className} ${mobileView && !textAlign ? style.textRight : style.textLeft} `}
                    style={textAlign ? { textAlign } : {}}
                    onKeyDown={(e) =>
                    {
                        const { code } = e;
                        onKeyDown && onKeyDown(e);
                        if (!code || code.toLowerCase() !== "enter") return;
                        const value = getInputValue();
                        if (onEnter) onEnter(value || "");
                    }}
                    onIonInput={({ detail }) =>
                    {
                        if (debounce) handleChange(detail);
                    }}
                    onIonChange={({ detail }) =>
                    {
                        if (!debounce) handleChange(detail);
                    }}
                >
                    {icon && <IonButton onClick={onIconClick} fill="default" slot={mobileView ? "start" : "end"} aria-label="Find">
                        <IonIcon slot="icon-only" icon={icon} aria-hidden="true"></IonIcon>
                    </IonButton>}
                </IonInput>
            </div>
        </div>

    </IonItem>;
};

export default SettingsCheckboxTextInput;
